/**
 * ASSET DETAIL. One marketplace listing, in full.
 *
 * The invest panel is honest about what it is in this phase: it SHOWS what a stake would look like
 * (units, cost, projected income) and routes the intent, but it does not itself move value. A real
 * investment settles through the compliance spine under escrow, like every other movement, and
 * re-confirms price at that moment. The panel says so rather than pretending the button is the trade.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { api, type RiskBand } from '@/lib/api';
import { StatTile as Stat } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal } from '@/components/motion';
import { Sheet } from '@/components/overlay';

const TONE: Record<string, string> = {
  emerald: 'from-emerald-500 to-emerald-700',
  amber: 'from-amber-400 to-amber-600',
  lime: 'from-lime-500 to-lime-700',
  sky: 'from-sky-500 to-sky-700',
  violet: 'from-violet-500 to-violet-700',
  teal: 'from-teal-500 to-teal-700',
};

const RISK: Record<RiskBand, { label: string; cls: string }> = {
  low: { label: 'Lower risk', cls: 'bg-accent/15 text-accent' },
  medium: { label: 'Medium risk', cls: 'bg-warning/15 text-warning' },
  high: { label: 'Higher risk', cls: 'bg-danger/10 text-danger' },
};

export default function ListingDetail() {
  const params = useParams();
  const {
    data: listing,
    loading,
    error,
    reload,
  } = useResource(() => api.listing(params.id ?? ''), [params.id]);
  const [amount, setAmount] = useState('');
  const [offerOpen, setOfferOpen] = useState(false);
  const [offer, setOffer] = useState('');

  const units = useMemo(() => {
    if (!listing || !amount) return 0;
    const a = Number(amount.replace(/,/g, ''));
    const p = Number(listing.pricePerToken.replace(/,/g, ''));
    return p > 0 ? Math.floor(a / p) : 0;
  }, [amount, listing]);

  if (loading) return <LoadingCard lines={5} />;
  if (error) return <ErrorState body="We could not load this asset." onRetry={reload} />;
  if (!listing) return null;

  const risk = RISK[listing.risk];

  return (
    <div className="space-y-5">
      <Link
        to="/marketplace"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Marketplace
      </Link>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        {/* MAIN */}
        <div className="space-y-5 lg:col-span-2">
          <Reveal>
            <div
              className={`relative flex min-h-[200px] flex-col justify-end overflow-hidden rounded-xl bg-gradient-to-br ${TONE[listing.tone] ?? TONE.emerald} p-5 text-white shadow-lifted`}
            >
              {listing.image ? (
                <img
                  src={listing.image}
                  alt=""
                  aria-hidden
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
              ) : null}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-12 h-48 w-48 rounded-full bg-white/15 blur-3xl"
              />
              <div className="relative flex items-center gap-2">
                <span className="rounded-pill bg-white/20 px-2.5 py-0.5 text-micro font-medium backdrop-blur">
                  {listing.category}
                </span>
                <span className="rounded-pill bg-white/90 px-2.5 py-0.5 text-micro font-medium text-ink">
                  {risk.label}
                </span>
              </div>
              <h1 className="relative mt-3 text-2xl font-semibold tracking-tight">
                {listing.title}
              </h1>
              <p className="relative mt-1 text-sm text-white/85">{listing.location}</p>
            </div>
          </Reveal>

          {/* KEY STATS */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Projected yield" value={`${listing.yieldPct}%`} accent />
            <Stat label="Per token" value={`KES ${listing.pricePerToken}`} />
            <Stat label="Minimum" value={`KES ${listing.minInvest}`} />
            <Stat label="Investors" value={String(listing.investors)} />
          </div>

          {/* FUNDING */}
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <div className="flex items-baseline justify-between">
              <h2 className="font-semibold text-ink">Funding progress</h2>
              <span className="tabular text-sm font-medium text-ink">{listing.fundedPct}%</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-sunken">
              <div
                className="h-full rounded-full bg-accent"
                style={{ width: `${listing.fundedPct}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-xs text-ink-muted">
              <span>KES {listing.raised} raised</span>
              <span>Target KES {listing.target}</span>
            </div>
          </section>

          {/* TRUST ENTITY. The legal bridge between the real asset and the token. */}
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-brand">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.8}
                    d="M12 3 4 6v5c0 4.5 3.2 8 8 10 4.8-2 8-5.5 8-10V6l-8-3Z"
                  />
                </svg>
              </span>
              <div>
                <h2 className="font-semibold text-ink">{listing.trust}</h2>
                <p className="text-xs text-ink-muted">
                  The regulated trust that legally holds this asset
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-ink-muted">
              You are buying a fraction of this trust, not the asset directly. The trust owns the
              underlying property and issues tokenized shares against it, so your ownership is
              recorded, enforceable, and independent of the platform. This is the same structure
              institutional tokenization uses.
            </p>
            <button className="mt-3 text-sm font-medium text-brand hover:underline">
              View trust certificate and legal documents
            </button>
          </section>
        </div>

        {/* INVEST PANEL. Sticky on desktop, inline on mobile. */}
        <aside className="lg:sticky lg:top-20">
          <div className="rounded-xl border border-border bg-surface p-5 shadow-soft">
            <p className="text-sm text-ink-muted">Invest in this asset</p>
            <div className="mt-2">
              <label htmlFor="amt" className="sr-only">
                Amount in KES
              </label>
              <div className="flex items-center gap-2 rounded-field border border-border px-3 py-2 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
                <span className="text-sm text-ink-muted">KES</span>
                <input
                  id="amt"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder={listing.minInvest}
                  className="w-full font-mono text-sm outline-none"
                />
              </div>
              <p className="mt-2 text-sm text-ink-muted">
                {units > 0 ? (
                  <>
                    Buys <span className="font-medium text-ink">{units.toLocaleString()}</span>{' '}
                    token
                    {units === 1 ? '' : 's'} of {listing.title}
                  </>
                ) : (
                  <>
                    From KES {listing.minInvest}, in KES {listing.pricePerToken} tokens
                  </>
                )}
              </p>
            </div>

            <button className="mt-4 w-full rounded-field bg-primary py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light">
              Review investment
            </button>
            <button
              onClick={() => setOfferOpen(true)}
              className="mt-2 w-full rounded-field border border-border py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
            >
              Make an offer
            </button>

            {/* Honest about what this phase can and cannot do. */}
            <p className="mt-3 rounded-lg bg-surface-sunken p-3 text-xs leading-relaxed text-ink-muted">
              Investing settles through the platform&apos;s compliance and escrow checks, and the
              price is confirmed again at that moment. This activates in a later sandbox phase.
            </p>
          </div>
        </aside>
      </div>

      {/* MAKE AN OFFER. Bid a price per token below the ask; the trust reviews it. */}
      <Sheet
        open={offerOpen}
        onClose={() => setOfferOpen(false)}
        title={`Make an offer on ${listing.title}`}
      >
        <div className="space-y-4">
          <dl className="flex justify-between rounded-field bg-surface-sunken px-3 py-2 text-sm">
            <dt className="text-ink-muted">Asking price</dt>
            <dd className="tabular font-medium text-ink">KES {listing.pricePerToken} / token</dd>
          </dl>
          <div>
            <label htmlFor="offer" className="block text-sm font-medium text-ink">
              Your price per token
            </label>
            <div className="mt-1 flex items-center gap-2 rounded-field border border-border px-3 py-2.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25">
              <span className="text-sm text-ink-muted">KES</span>
              <input
                id="offer"
                autoFocus
                inputMode="decimal"
                value={offer}
                onChange={(e) => setOffer(e.target.value.replace(/[^\d.]/g, ''))}
                placeholder={listing.pricePerToken}
                className="w-full bg-transparent font-mono text-base text-ink outline-none"
              />
            </div>
          </div>
          <button
            onClick={() => setOfferOpen(false)}
            disabled={!offer}
            className="w-full rounded-field bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-light disabled:opacity-50"
          >
            Submit offer
          </button>
          <p className="text-center text-xs text-ink-subtle">
            The trust reviews offers and may accept, counter, or decline.
          </p>
        </div>
      </Sheet>
    </div>
  );
}
